import cn from "classnames";
import BookShow from "components/shared/book/show";
import { CargoOwnerDocsForm } from "components/shared/cargoOwnerDocs";
import CargoOwnerShareAccess from "components/shared/cargoOwnerShareAccess";
import Loader from "components/shared/loader";
import { counterpartyFlags, counterpartyFlagsEnum, featureModuleTypesEnum } from "helpers/enums";
import { serializeReq } from "helpers/form";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { flagVal, ymEvent } from "helpers/system";
import { requiredField } from "helpers/validators";
import React from "react";
import { CustomScroll } from "react-custom-scroll";
import { NavLink, Route, Switch } from "react-router-dom";
import { giveAccessToBids, removeAccessToBids } from "sources/books/cargoOwners";
import { checkDocuments } from "sources/books/organizationDocuments";
import CounterpartyContracts from "../contracts";
import { config, initialState } from "../formConfig";
import { CounterpartyPlatformsForm, CounterpartyPlatformsShow } from "../platforms";
import CounterpartyPoints from "../points";
import Markup from "./markup";

const hasAccessToBidsFlagValue = flagVal(counterpartyFlags, counterpartyFlagsEnum.HasAccessToBids);

class ShowCargoOwner extends BookShow {
    componentDidMount() {
        this.context?.setPrevItemId(Number(this.props.match.params.id));
        const initial = this.props.initialState;
        this.setState({ data: initial, initial });
        this.getItem(this.props);
        checkDocuments().then((res) => this.setState({ documentsExists: res }));
    }

    getItem = ({ source, match }) => {
        const defParams = { isLoading: false, isValid: true, shouldUpdate: false };
        source.getItem(match.params).then((data) => {
            data.shareAccessEnabled = (hasAccessToBidsFlagValue & data.flags) === hasAccessToBidsFlagValue;
            this.setState({ data, initial: data, ...defParams, platformsShowMode: data.platforms.length > 0 });
        });
    };

    apply = () => {
        const { source, match, mergeParams, config } = this.props;
        const { isLoading, shouldUpdate, ...state } = this.state;

        const reqData = { ...state.data };

        if (!reqData.contactPerson || Object.values(reqData.contactPerson).every((val) => !val || val === "+")) {
            reqData.contactPerson = null;
        }

        if (!reqData.management || Object.values(reqData.management).every((val) => !val)) {
            reqData.management = null;
        }

        if (reqData.isInsolvent && !this.state.initial.isInsolvent) {
            ymEvent("InsolventCounterparty");
        }

        if (!isLoading && shouldUpdate) {
            if (this.isValidForm(reqData)) {
                this.setState({ isLoading: true, isValid: true });
                return source
                    .apply(serializeReq({ id: match.params, ...reqData }, config))
                    .then((res) =>
                        res ? this.update(mergeParams) : this.setState({ isLoading: false, isValid: false }),
                    );
            }
            this.setState({ isValid: false });
        }
    };

    get dropdActions() {
        const actions = [{ text: "Печать", action: this.actionPrint }];
        if (!this.props.canEditBooks) {
            return actions;
        }
        const { shareAccessEnabled } = this.state.data;
        const {
            profile,
            roles: { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist, isTruckingOrganizationManager },
        } = this.props;
        if (
            profile.availableFeatureModules.includes(featureModuleTypesEnum.Forwarding) &&
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist || isTruckingOrganizationManager) &&
            !shareAccessEnabled
        ) {
            actions.push({ text: "Открыть доступ к заявкам", action: this.openAccessAction });
        }
        actions.push({ text: "Удалить", action: this.actionClose });

        return actions;
    }

    actionPrint = () => {
        const { modal, config } = this.props;
        return modal({
            name: "print",
            title: "Печать контрагента",
            CustomBody: Markup,
            bodyData: {
                ...config,
                renderDocuments: this.renderDocuments,
                values: this.state.initial,
                children: (
                    <>
                        {this.state.data.comment && (
                            <div className="row form-group">
                                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                                    Комментарий
                                </label>
                                <div className="col-sm-8 col-md-9 col-form-label-sm">
                                    <i className="text-muted">{this.state.data.comment}</i>
                                </div>
                            </div>
                        )}
                        {this.state.data.documentTemplates && (
                            <div className="row form-group">
                                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                                    Шаблоны документов
                                </label>
                                <div className="col-sm-8 col-md-9 col-form-label-sm">
                                    {this.state.data.documentTemplates.map((item) => (
                                        <div key={item.id}>
                                            <a
                                                href={paths.documentTemplatesShow(item)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {item.name}
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <CounterpartyPlatformsShow platforms={this.state.data.platforms} />
                    </>
                ),
            },
        });
    };

    openAccessAction = () => {
        const { data } = this.state;
        const { modal } = this.props;
        return modal({
            name: "default",
            title: "Предоставление доступа к заявкам",
            CustomBody: CargoOwnerShareAccess,
            bodyData: {
                submit: giveAccessToBids,
                initialState: {
                    shareAccessEnabled: true,
                    id: data.id,
                    kpp: data.kpp,
                    name: data.name,
                    inn: data.inn,
                    kppDisabled: !!data.kpp,
                    contactPerson: data.contactPerson,
                },
            },
            onCloseAction: () => this.getItem(this.props),
        });
    };

    syncLegalInformation = () => {
        if (!this.props.canEditBooks || this.state.shouldUpdate) {
            return;
        }

        this.props.modal({
            name: "confirm",
            title: "Обновление данных из Dadata",
            text: "Данные для документов будут повторно запрошены из Dadata и перезаписаны в карточке контрагента.",
            action: () => this.props.source.syncLegalInformation({ id: this.state.data.id }),
            onSuccessAction: () => this.getItem(this.props),
            buttons: { confirm: "Обновить", cancel: "Отмена" },
        });
    };

    renderDocuments = () => {
        const { documentsExists } = this.state;
        if (!documentsExists) {
            return null;
        }
        const props = {
            documents: this.state.data.documents,
            setValue: this.setValue,
            disabled: !this.props.canEditBooks,
        };
        return (
            <div className="row form-group">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    Документы
                </label>
                <div className="col-sm-8 col-md-9">
                    <CargoOwnerDocsForm {...props} />
                </div>
            </div>
        );
    };

    renderBodyWrap = () => {
        const { isLoading } = this.state;
        if (isLoading) {
            return <Loader prefix="absolute-center" />;
        }
        const { match, roles } = this.props;
        return (
            <div className="d-flex flex-column h-100">
                <div className="container-fluid">
                    <div className="table-tabs m-0 mb-3">
                        <NavLink
                            exact
                            to={paths.cargoOwnersShow(this.state.data.id)}
                            className="table-tabs__item"
                            activeClassName="table-tabs__item--active"
                        >
                            Информация о контрагенте
                        </NavLink>
                        {(roles.isTruckingOrganizationAdmin ||
                            roles.isTruckingOrganizationManager ||
                            roles.isTruckingOrganizationHeadLogist ||
                            roles.isTruckingOrganizationOperator) && (
                            <NavLink
                                to={paths.counterpartyPoints(this.state.data.id)}
                                className="table-tabs__item"
                                activeClassName="table-tabs__item--active"
                            >
                                Адреса контрагента
                            </NavLink>
                        )}
                        <NavLink
                            to={paths.counterpartyContracts(this.state.data.id)}
                            className="table-tabs__item"
                            activeClassName="table-tabs__item--active"
                        >
                            Договоры контрагента
                        </NavLink>
                    </div>
                </div>
                <div className="flex-grow-1 overflow-hidden">
                    <Switch>
                        <Route
                            path={`${paths.cargoOwnersShow(":counterpartyId")}/contracts`}
                            render={(rest) => <CounterpartyContracts {...rest} />}
                        />
                        <Route
                            path={`${paths.cargoOwnersShow(":counterpartyId")}/points`}
                            render={(rest) => <CounterpartyPoints {...rest} />}
                        />
                        <Route
                            exact
                            path={match.path}
                            render={() => (
                                <CustomScroll heightRelativeToParent="100%">{this.renderBody()}</CustomScroll>
                            )}
                        />
                    </Switch>
                </div>
            </div>
        );
    };

    applyForm = () => {
        ymEvent("CounterpartyChangeSaved");
        this.apply();
    };

    renderButton = () => {
        const cname = cn("btn btn-brand btn-sm", { disabled: !this.state.shouldUpdate });
        const syncButtonClass = cn("btn btn-secondary btn-sm mr-2", { disabled: this.state.shouldUpdate });
        return (
            <div className="panel__row panel__row--button">
                {this.props.canEditBooks && !this.state.data.isForeign && (
                    <div
                        className={syncButtonClass}
                        onClick={this.syncLegalInformation}
                        title={this.state.shouldUpdate ? "Сначала сохраните изменения" : undefined}
                    >
                        Обновить из Dadata
                    </div>
                )}
                <div className={cname} onClick={this.applyForm}>
                    Обновить
                </div>
            </div>
        );
    };

    removeAccess = () => {
        this.props.modal({
            name: "confirm",
            title: "Вы действительно хотите закрыть доступ к заявкам?",
            action: () => removeAccessToBids({ id: this.state.data.id }),
            onSuccessAction: () => this.getItem(this.props),
            buttons: { confirm: "Да", cancel: "Нет" },
        });
    };

    onContactPersonChange = ({ name, value }) =>
        this.setValue({ name: "contactPerson", value: { ...this.state.data.contactPerson, [name]: value } });

    onManagementChange = ({ name, value }) =>
        this.setValue({ name: "management", value: { ...(this.state.data.management || {}), [name]: value } });

    renderManagement = () => {
        const management = this.state.data.management || {};
        const { config } = this.props.config.management;

        return (
            <div className="row form-group">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    Руководитель
                </label>
                <div className="col-sm-8 col-md-9 col-form-label-sm">
                    {this.renderField({
                        ...config.position,
                        onChange: this.onManagementChange,
                        value: management.position || "",
                        labelWrapper: "col-12 text-muted",
                        fieldWrapper: "col-12",
                    })}
                    {this.renderField({
                        ...config.lastName,
                        onChange: this.onManagementChange,
                        value: management.lastName || "",
                        labelWrapper: "col-12 text-muted",
                        fieldWrapper: "col-12",
                    })}
                    {this.renderField({
                        ...config.firstName,
                        onChange: this.onManagementChange,
                        value: management.firstName || "",
                        labelWrapper: "col-12 text-muted",
                        fieldWrapper: "col-12",
                    })}
                    {this.renderField({
                        ...config.patronymic,
                        onChange: this.onManagementChange,
                        value: management.patronymic || "",
                        labelWrapper: "col-12 text-muted",
                        fieldWrapper: "col-12",
                    })}
                </div>
            </div>
        );
    };

    renderBody = () => {
        const { config, enums } = this.props;

        const contactPersonValidatorsConfig =
            this.state.data.contactPerson && Object.values(this.state.data.contactPerson).some((val) => !!val)
                ? {
                      required: true,
                      validators: [requiredField],
                  }
                : {
                      required: false,
                      validators: undefined,
                  };

        return (
            <div className="container-fluid pb-3">
                <div className="card card-body">
                    <Markup
                        {...config}
                        renderDocuments={this.renderDocuments}
                        renderField={this.renderField}
                        renderSelect={this.renderSelect}
                        renderCheckbox={this.renderCheckbox}
                        renderManagement={this.renderManagement}
                        values={this.state.data}
                        Button={this.renderButton}
                    >
                        {this.state.data.documentTemplates && this.state.data.documentTemplates.length > 0 && (
                            <div className="row form-group">
                                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                                    Шаблоны документов
                                </label>
                                <div className="col-sm-8 col-md-9 col-form-label-sm">
                                    {this.state.data.documentTemplates.map((item) => (
                                        <div key={item.id}>
                                            <a
                                                href={paths.documentTemplatesShow(item)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {item.name}
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!!this.state.data.platforms?.length && this.state.platformsShowMode ? (
                            <>
                                <CounterpartyPlatformsShow platforms={this.state.data.platforms}>
                                    <div
                                        className="btn btn-xs btn-secondary"
                                        onClick={() => this.setState({ platformsShowMode: false })}
                                    >
                                        Изменить
                                    </div>
                                </CounterpartyPlatformsShow>
                            </>
                        ) : (
                            <>
                                <CounterpartyPlatformsForm
                                    platforms={this.state.data.platforms}
                                    onChange={this.setValue}
                                    enums={enums}
                                >
                                    {(this.state.data.platforms?.length !== this.state.initial.platforms.length ||
                                        !!this.state.data.platforms?.length) && (
                                        <div
                                            className="btn btn-xs btn-secondary"
                                            onClick={() =>
                                                this.setState({
                                                    platformsShowMode: this.state.initial.platforms.length > 0,
                                                    data: {
                                                        ...this.state.data,
                                                        platforms: this.state.initial.platforms,
                                                    },
                                                })
                                            }
                                        >
                                            Отменить
                                        </div>
                                    )}
                                </CounterpartyPlatformsForm>
                            </>
                        )}
                        {this.state.data.shareAccessEnabled && (
                            <div className="row form-group">
                                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                                    Предоставлен доступ к заявкам
                                </label>
                                <div className="col-sm-8 col-md-9 col-form-label-sm">
                                    Да
                                    <span className="btn btn-xs btn-outline-danger ml-2" onClick={this.removeAccess}>
                                        Закрыть доступ
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="row form-group">
                            <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                                Контактные данные
                            </label>
                            <div className="col-sm-8 col-md-9 col-form-label-sm">
                                {this.renderField({
                                    ...config.contactPerson.config.firstName,
                                    ...contactPersonValidatorsConfig,
                                    alwaysEnabled: true,
                                    onChange: this.onContactPersonChange,
                                    value: this.state.data.contactPerson?.firstName || "",
                                    labelWrapper: "col-12 text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {this.renderField({
                                    ...config.contactPerson.config.lastName,
                                    ...contactPersonValidatorsConfig,
                                    alwaysEnabled: true,
                                    onChange: this.onContactPersonChange,
                                    value: this.state.data.contactPerson?.lastName || "",
                                    labelWrapper: "col-12 text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {this.renderField({
                                    ...config.contactPerson.config.patronymic,
                                    alwaysEnabled: true,
                                    onChange: this.onContactPersonChange,
                                    value: this.state.data.contactPerson?.patronymic || "",
                                    labelWrapper: "col-12 text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {this.renderField({
                                    ...config.contactPerson.config.phoneNumber,
                                    ...contactPersonValidatorsConfig,
                                    alwaysEnabled: true,
                                    onChange: this.onContactPersonChange,
                                    value: this.state.data.contactPerson?.phoneNumber || "",
                                    labelWrapper: "col-12 text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {this.renderField({
                                    ...config.contactPerson.config.email,
                                    ...contactPersonValidatorsConfig,
                                    alwaysEnabled: true,
                                    onChange: this.onContactPersonChange,
                                    value: this.state.data.contactPerson?.email || "",
                                    labelWrapper: "col-12 text-muted",
                                    fieldWrapper: "col-12",
                                })}
                            </div>
                        </div>
                    </Markup>
                </div>
            </div>
        );
    };

    render() {
        const cname = cn("book-show__body", { "book-show__body--invalid": !this.state.isValid });
        return (
            <div className="page-col page-col--full mounted">
                <div className="page-book__show">
                    <div className="container-fluid">{this.renderHead()}</div>
                    <div className={cname}>{this.renderBodyWrap()}</div>
                </div>
            </div>
        );
    }
}

const ConfiguredShow = (props) => <ShowCargoOwner {...props} config={config} initialState={initialState} />;

export default viewContainer(ConfiguredShow, "cargoowners");
